import SearchEmpty from "@images/empty/search_empty.svg"
import { CrossIcon } from "@images/svgIcons/crossIcon"
import { DuplicateIcon } from "@images/svgIcons/duplicateIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { PencilIcon } from "@images/svgIcons/pencil"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { useProgramOnOff, useProgramsOnOff } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramOnOffFormikType, ProgramOnOffInputKey } from "@modules/programs/programOnOffFormikType"
import { ItemProgramOnOff } from "@modules/programs/programOnOffTypes"
import { HUBackgroundImageView } from "@modules/ui/components/huBackgroundImageView"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { SimpleFormik } from "@src/typings/formik"
import { SearchIcon } from "lucide-react"
import { InputText } from "primereact/inputtext"
import { classNames } from "primereact/utils"
import { useMemo, useState } from "react"
import { useTheme } from "styled-components"

type ProgramOnOffCopyProps = {
  formik: SimpleFormik<ProgramOnOffFormikType, ProgramOnOffInputKey>
} & React.HTMLAttributes<HTMLDivElement>

export const ProgramOnOffCopy: React.FC<ProgramOnOffCopyProps> = ({ formik }) => {
  const [dialogIsVisible, setDialogIsVisible] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState<ItemProgramOnOff | null>(null)
  const [searchTerms, setSearchTerms] = useState<string>("")

  const { result: onOffPrograms, loading } = useProgramsOnOff({ programFilter: "OWNED", size: 100 })
  const { result: onOffProgramToDuplicate, loading: loadingProgram } = useProgramOnOff(selectedProgram?.id)
  const theme = useTheme()

  const filteredOnOffPrograms = useMemo(() => {
    if (onOffPrograms) {
      return onOffPrograms.filter((program) => program.title.toLowerCase().includes(searchTerms.toLowerCase()))
    }
    return []
  }, [onOffPrograms, searchTerms])

  const toggleSelectedProgramId = (program: ItemProgramOnOff) => {
    if (program.id === selectedProgram?.id) {
      setSelectedProgram(null)
    } else {
      setSelectedProgram(program)
    }
  }

  const duplicateOnOffProgram = () => {
    if (onOffProgramToDuplicate) {
      formik.setFieldValue("programIdToDuplicate", onOffProgramToDuplicate.id)
      formik.setFieldValue("image", onOffProgramToDuplicate.image)
      formik.setFieldValue("title", `${onOffProgramToDuplicate.title} (${word("global.copied")})`)
      formik.setFieldValue("description", onOffProgramToDuplicate.description)
      formik.setFieldValue("duration", onOffProgramToDuplicate.duration)
      formik.setFieldValue("entranceCheckEnabled", onOffProgramToDuplicate.entranceCheckEnabled)
      formik.setFieldValue("sharedCoaches", onOffProgramToDuplicate.sharedCoaches)
    }
  }

  const onConfirmDialog = () => {
    duplicateOnOffProgram()
    setDialogIsVisible(false)
  }

  const clearSelection = () => {
    setSelectedProgram(null)
    formik.setFieldValue("programIdToDuplicate", null)
  }

  const closeDialog = () => {
    setSearchTerms("")
    setDialogIsVisible(false)
  }

  return (
    <>
      {/* Display duplicated program */}
      {selectedProgram && formik.values.programIdToDuplicate ? (
        <div className="mb-3">
          <HUText fontStyle="LS">{word("program.form.on-off.duplicateFrom")}</HUText>

          <div className="flex flex-row justify-content-between align-items-center mt-2">
            <div className="flex flex-row justify-content-start align-items-center gap-2">
              <HUBackgroundImageView
                image={{ path: selectedProgram.image.path }}
                width="63px"
                height="63px"
                className="flex align-items-end justify-content-center border-round-xl"
                style={{ backgroundRepeat: "repeat", backgroundSize: "cover" }}
              ></HUBackgroundImageView>

              <div className="flex flex-column">
                <HUText fontStyle="LL">{selectedProgram.title}</HUText>
                <HUText fontStyle="BS">{word("global.program.name")}</HUText>
              </div>
            </div>

            <div className="flex gap-2">
              <HUButton
                type="Rounded"
                colorType="Secondary"
                size="XS"
                icon={{ icon: PencilIcon }}
                onClick={() => setDialogIsVisible(true)}
              />
              <HUButton
                type="Rounded"
                colorType="Secondary"
                size="XS"
                icon={{ icon: TrashIcon }}
                onClick={clearSelection}
              />
            </div>
          </div>
        </div>
      ) : (
        /* Display duplicate a program button */
        <HUButton
          colorType="Secondary"
          size="XS"
          type="Left_Icon"
          icon={{ icon: DuplicateIcon }}
          disabled={loading || onOffPrograms.length === 0}
          className="mb-3 px-3"
          text={word("program.form.on-off.duplicateAnotherProgram")}
          loading={loading}
          onClick={() => setDialogIsVisible(true)}
        />
      )}

      {/* Modal to select the program to duplicate */}
      <HUDialog
        visible={dialogIsVisible}
        header={word("program.form.on-off.duplicateAProgram")}
        onHide={closeDialog}
        style={{ height: 650, width: 650 }}
        headerStyle={{ textAlign: "center", paddingBottom: "34px" }}
        contentStyle={{ padding: "0px 1.5rem" }}
      >
        <div className="flex w-full mb-5">
          <WarningHexagonalIcon className="mr-3" color={theme.neutralColor400} size={24} />
          <HUText fontStyle="BM" color={theme.textSecondary}>
            {word("program.form.on-off.duplicateExplanation")}
          </HUText>
        </div>

        <div className="flex w-full mb-5">
          <div className=" flex align-items-center w-full h-3rem rounded-full border-neutral-300 bg-neutral-100">
            <div className="flex align-items-center justify-content-center w-3rem h-full ">
              <SearchIcon color={theme.neutralColor700} width={14} height={14} />
            </div>
            <InputText
              className="w-full h-full p-0 m-0 border-none outline-none bg-neutral-100"
              placeholder={word("program.label.title")}
              value={searchTerms}
              style={{ borderRadius: "30px" }}
              onChange={(e) => setSearchTerms(e.currentTarget.value)}
            />
            {searchTerms && (
              <button
                onClick={() => setSearchTerms("")}
                className="flex align-items-center justify-content-center w-3rem h-full "
              >
                <CrossIcon color={theme.neutralColor700} width={14} height={14} />
              </button>
            )}
          </div>
        </div>

        <div style={{ height: 330, overflow: "scroll" }}>
          {filteredOnOffPrograms.length > 0 ? (
            filteredOnOffPrograms.map((program) => (
              <div
                key={program.id}
                className={classNames(
                  "flex flex-row align-items-center mb-2 gap-2 px-2 py-2 cursor-pointer border-round-xl hover:surface-100",
                  {
                    "bg-neutral-100": selectedProgram?.id === program.id,
                  },
                )}
                onClick={() => toggleSelectedProgramId(program)}
              >
                <HUBackgroundImageView
                  image={{ path: program.image.path }}
                  width="63px"
                  height="63px"
                  className="flex align-items-end justify-content-center border-round-xl"
                  style={{ backgroundRepeat: "repeat", backgroundSize: "cover" }}
                >
                  <OnOffIcon color={theme.mainWhite} width={20} height={20} />
                </HUBackgroundImageView>

                <div className="flex flex-column">
                  <HUText fontStyle="BM" color={theme.textPrimary}>
                    {program.title}
                  </HUText>
                  <HUText fontStyle="BS" color={theme.neutralColor400}>
                    {word("global.program.name")}
                  </HUText>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-content-center flex-column mt-4">
              <HUText fontStyle="LL" color={theme.neutralColor700} className="flex justify-content-center text-center">
                {word("program.form.on-off.duplicateProgramNotFound")}
              </HUText>
              <div className="flex justify-content-center text-center align-items-center mt-4">
                <img src={SearchEmpty} alt="SessionEmptySVG" width={200} />
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 flex justify-content-center gap-4 align-items-center">
          <HUButton colorType="Quaternary" size="M" type="Default" text={word("global.cancel")} onClick={closeDialog} />
          <HUButton
            colorType="Primary"
            size="M"
            type="Left_Icon"
            text={word("program.form.on-off.duplicate")}
            disabled={!selectedProgram || loadingProgram}
            onClick={onConfirmDialog}
            icon={{
              iconView: <span className={`pi pi-check`} />,
            }}
          />
        </div>
      </HUDialog>
    </>
  )
}
